import React from 'react';
import { Link } from 'react-router-dom';
import { Coins, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CreditsDisplayProps {
  balance: number | null;
  allocatedCredits?: number | null;
  isLoading?: boolean;
  isStreaming?: boolean;
  className?: string;
}

export function CreditsDisplay({ balance, allocatedCredits, isLoading, isStreaming, className }: CreditsDisplayProps) {
  // Don't show anything when loading or when agent is processing
  if (isLoading || isStreaming) {
    return null;
  }

  if (balance === null || balance === undefined) {
    return null;
  }

  // Parse balance (can be string from PostgreSQL decimal)
  const numBalance = typeof balance === 'string' ? parseFloat(balance) : balance;
  if (typeof numBalance !== 'number' || isNaN(numBalance)) {
    return null;
  }

  // Calculate warning threshold: 10% of allocated credits
  const numAllocated = allocatedCredits
    ? (typeof allocatedCredits === 'string' ? parseFloat(allocatedCredits) : allocatedCredits)
    : 0;
  const warningThreshold = numAllocated ? numAllocated * 0.1 : 0.5;
  const isLow = numBalance < warningThreshold;

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
        isLow
          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/80 dark:text-amber-400'
          : 'bg-primary/5 dark:bg-primary/15 text-foreground',
        className
      )}
    >
      <Coins className="w-3.5 h-3.5" />
      <span className="font-mono">{numBalance.toFixed(2)}/{numAllocated.toFixed(2)}</span>
    </span>
  );
}

export type CreditAlertLevel = 'warning' | 'critical' | 'insufficient';

interface CreditsWarningProps {
  balance: number | string;
  allocatedCredits?: number | string;
  onGetCredits?: () => void;
  isTyping?: boolean;
  /** Which alerts have already been shown (80%, 90%) - insufficient always shows */
  shownAlerts?: Set<CreditAlertLevel>;
  /** Called when an alert is shown for the first time */
  onAlertShown?: (level: CreditAlertLevel) => void;
  /** When true, hide all credit warnings (BYOC users pay Anthropic directly) */
  byocConnected?: boolean;
}

/**
 * Determine the current alert level based on credit balance
 * - warning: 80% used (20% remaining)
 * - critical: 90% used (10% remaining)
 * - insufficient: balance <= 0 (negative or zero)
 */
function getAlertLevel(balance: number, allocatedCredits: number): CreditAlertLevel | null {
  // Insufficient: balance is 0 or negative
  if (balance <= 0) {
    return 'insufficient';
  }

  const percentRemaining = (balance / allocatedCredits) * 100;

  // Critical: 10% or less remaining (90%+ used)
  if (percentRemaining <= 10) {
    return 'critical';
  }

  // Warning: 20% or less remaining (80%+ used)
  if (percentRemaining <= 20) {
    return 'warning';
  }

  return null;
}

export function CreditsWarning({
  balance,
  allocatedCredits,
  onGetCredits,
  isTyping,
  shownAlerts,
  onAlertShown,
  byocConnected = false,
}: CreditsWarningProps) {
  // BYOC users pay Anthropic directly - hide all credit warnings
  if (byocConnected) {
    return null;
  }

  // Parse balance (can be string from PostgreSQL decimal)
  const numBalance = typeof balance === 'string' ? parseFloat(balance) : balance;
  const numAllocated = typeof allocatedCredits === 'string' ? parseFloat(allocatedCredits) : allocatedCredits;

  const currentLevel = (typeof numBalance === 'number' && !isNaN(numBalance))
    ? (numAllocated && !isNaN(numAllocated) && numAllocated > 0
        ? getAlertLevel(numBalance, numAllocated)
        : numBalance <= 0 ? 'insufficient' : null)
    : null;

  const isInsufficient = currentLevel === 'insufficient';

  // Check if this level was already shown before
  const wasAlreadyShown = currentLevel && shownAlerts?.has(currentLevel);

  // When user starts typing, mark the alert as shown (for 80%/90% alerts only)
  React.useEffect(() => {
    if (isTyping && currentLevel && !isInsufficient && onAlertShown && !shownAlerts?.has(currentLevel)) {
      onAlertShown(currentLevel);
    }
  }, [isTyping, currentLevel, isInsufficient, onAlertShown, shownAlerts]);

  // Determine if we should show the alert
  // - Insufficient: always show (even when typing)
  // - 80%/90%: show until user types, then mark as shown and hide
  const shouldShow = currentLevel !== null &&
    (isInsufficient || (!isTyping && !wasAlreadyShown));

  // Don't render if shouldn't show
  if (!shouldShow || !currentLevel) {
    return null;
  }

  const getMessage = () => {
    if (isInsufficient) {
      return (
        <>
          <span className="text-red-600 dark:text-red-400">Insufficient credits:</span>{' '}
          <span className="font-mono font-medium text-red-600 dark:text-red-400">
            {numBalance.toFixed(2)}
          </span>
        </>
      );
    }
    if (currentLevel === 'critical') {
      return (
        <>
          90% of credits used.{' '}
          <span className="font-mono font-medium text-primary">{numBalance.toFixed(2)}</span> remaining
        </>
      );
    }
    // Warning level
    return (
      <>
        80% of credits used.{' '}
        <span className="font-mono font-medium text-primary">{numBalance.toFixed(2)}</span> remaining
      </>
    );
  };

  return (
    <div className={cn(
      "flex flex-col gap-2 rounded-lg border px-3 py-2 text-xs",
      isInsufficient
        ? "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/50"
        : "border-primary/30 bg-primary/5 dark:border-primary/30 dark:bg-primary/10",
      "text-foreground"
    )}>
      <div className="flex items-center justify-between gap-3">
        <span>{getMessage()}</span>
        {onGetCredits && (
          <button
            onClick={onGetCredits}
            className="font-medium rounded-md bg-primary text-white hover:bg-primary/90 transition-colors px-2.5 py-1 text-xs shrink-0"
          >
            Upgrade
          </button>
        )}
      </div>
      <Link
        to="/dashboard/settings/workspace?claude=reconnect"
        className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
      >
        <Sparkles className="h-3 w-3 text-purple-500 shrink-0" />
        <span>
          Have a Claude subscription?{' '}
          <span className="text-purple-600 dark:text-purple-400 hover:underline font-medium">
            Connect for unlimited usage
          </span>
        </span>
      </Link>
    </div>
  );
}
